import React from 'react';
import { Phone, MessageCircle, MapPin, Clock, Heart, Sparkles } from 'lucide-react';
import { CONTACT_INFO } from '../data/services';
import { navigateTo } from '../utils/router';

export const Footer: React.FC = () => {
  return (
    <footer id="contact" className="footer">
      <div className="section-container">
        {/* ── Mobile App-Style Catchy Contact & Location Card (Active on <= 768px) ── */}
        <div className="mobile-app-contact-card">
          <div className="mobile-contact-header">
            <h4 className="mobile-contact-title">Doorstep Parlour Care</h4>
            <span className="mobile-contact-sub">Verified female specialists right to your home</span>
          </div>

          <div className="mobile-contact-pills">
            <span className="mobile-contact-pill">
              <MapPin size={11} color="var(--blishka-berry)" />
              <span>{CONTACT_INFO.serviceAreas}</span>
            </span>
            <span className="mobile-contact-pill">
              <Clock size={11} color="var(--blishka-berry)" />
              <span>{CONTACT_INFO.hours}</span>
            </span>
          </div>

          <div className="mobile-contact-actions">
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="mobile-contact-btn call-btn"
            >
              <Phone size={14} />
              <span>Call Now</span>
            </a>
            <a
              href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent('Hi Blishka! I would like to book a home beauty appointment.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-contact-btn wa-btn"
            >
              <MessageCircle size={14} />
              <span>WhatsApp Us</span>
            </a>
          </div>
        </div>

        {/* ── Desktop Detailed Footer Grid (Active on > 768px) ── */}
        <div className="footer-grid desktop-footer-grid">
          {/* Brand Info */}
          <div>
            <div
              className="nav-brand-container"
              style={{ marginBottom: '0.75rem', cursor: 'pointer' }}
              onClick={() => navigateTo('/')}
            >
              <div className="brand-logo-mark">
                <img src="/brand-logo.png" alt="Blishka Brand Icon" className="brand-logo-img" />
              </div>
              <div className="brand-text-block">
                <span className="nav-brand-title">
                  Blishka
                  <Sparkles size={16} color="var(--blishka-berry-light)" />
                </span>
                <span className="nav-brand-tagline">blissfully you!!</span>
              </div>
            </div>
            <p style={{ fontSize: '0.92rem', color: 'var(--color-text-body)', lineHeight: 1.6, maxWidth: '340px' }}>
              Premium doorstep salon &amp; parlour services brought directly to your home with complete hygiene, single-use disposables, and experienced female beauty professionals.
            </p>
            <span className="footer-quote">
              Because you deserve to feel good &hearts;
            </span>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="footer-col-title">Popular Services</h4>
            <ul className="footer-links-list">
              <li>
                <a
                  href="/services"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/services', { category: 'threading' });
                  }}
                >
                  Eyebrow &amp; Facial Threading
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/services', { category: 'waxing' });
                  }}
                >
                  Full Body &amp; Arms Waxing
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/services', { category: 'facial' });
                  }}
                >
                  Glow &amp; O3 Facials
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/services', { category: 'manicure' });
                  }}
                >
                  Spa Manicure &amp; Pedicure
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/services', { category: 'hairspa' });
                  }}
                >
                  Nourishing Hair Spa
                </a>
              </li>
            </ul>
          </div>

          {/* Bundles */}
          <div>
            <h4 className="footer-col-title">Blishka Bundles</h4>
            <ul className="footer-links-list">
              <li>
                <a
                  href="/bundles"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/bundles', { packageId: 'bundle-everyday' });
                  }}
                >
                  Blishka Everyday (₹499)
                </a>
              </li>
              <li>
                <a
                  href="/bundles"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/bundles', { packageId: 'bundle-glow' });
                  }}
                >
                  Blishka Glow (₹599)
                </a>
              </li>
              <li>
                <a
                  href="/bundles"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/bundles', { packageId: 'bundle-wax-smooth' });
                  }}
                >
                  Blishka Wax &amp; Smooth (₹699)
                </a>
              </li>
              <li>
                <a
                  href="/bundles"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/bundles', { packageId: 'bundle-self-care' });
                  }}
                >
                  Blishka Self-Care (₹899)
                </a>
              </li>
              <li>
                <a
                  href="/bundles"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/bundles', { packageId: 'bundle-complete' });
                  }}
                >
                  Blishka Complete (₹1,199)
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & Hours */}
          <div>
            <h4 className="footer-col-title">Appointments &amp; Support</h4>
            <ul className="footer-links-list">
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <MapPin size={16} color="var(--blishka-berry)" style={{ marginTop: '3px', flexShrink: 0 }} />
                <span>{CONTACT_INFO.serviceAreas}</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} color="var(--blishka-berry)" style={{ flexShrink: 0 }} />
                <span>{CONTACT_INFO.hours}</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Phone size={16} color="var(--blishka-berry)" style={{ flexShrink: 0 }} />
                <a href={`tel:${CONTACT_INFO.phone}`} style={{ color: 'var(--blishka-berry)', fontWeight: 600 }}>
                  {CONTACT_INFO.displayPhone}
                </a>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <MessageCircle size={16} color="#25D366" style={{ flexShrink: 0 }} />
                <a
                  href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=Hi%20Blishka!%20I%20would%20like%20to%20book%20a%20home%20beauty%20appointment.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1eb857', fontWeight: 700 }}
                >
                  WhatsApp Booking
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom-bar">
          <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} Blishka Beauty &bull; All rights reserved.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--blishka-berry)', fontWeight: 600 }}>
            <span>Made with care for women in Bhilai & Durg</span>
            <Heart size={14} fill="var(--blishka-berry)" />
          </div>
        </div>
      </div>
    </footer>
  );
};
